import { StringContext } from "../../core/StringContext";
import { StringData } from "../../core/StringData";
import { StringModule } from "../../core/StringModule";
import { StringObject } from "../../objects/StringObject";
import { frameDOM } from "../../utils/frame-dom";
import { styleTxn } from "../../utils/style-txn";

export abstract class CursorReactiveModule extends StringModule {
  protected nearOnly = true;
  protected useAllObjects = false;
  protected maxDistanceMultiplier = 1;
  protected updateThreshold = 0.1;

  private objectDeltaData = new Map<StringObject, { dx: number; dy: number; dist: number }>();

  constructor(context: StringContext) {
    super(context);
  }

  override onObjectConnected(object: StringObject): void {
    super.onObjectConnected(object);
    this.centers.attach(object);
    this.hover.track(object);
  }

  override removeObject(id: string): void {
    const obj = this.objectMapOnPage.get(id);
    if (obj) {
      this.centers.detach(obj);
      this.hover.untrack(obj);
    }
    super.removeObject(id);
  }

  override onScroll(): void {
    this.centers.invalidateAll();
    this.scheduleCursorUpdate();
  }

  // onMouseMove(e: MouseEvent): void {
  //   this.scheduleUpdate();
  // }
  onMouseMoveMeasure(data: StringData) {
    super.onMouseMoveMeasure(data);
    this.refreshPointerState();
  }

  onMutate(data: StringData): void {
    super.onMutate(data);
  }

  protected getCursorTargets(): StringObject[] {
    if (this.nearOnly && this.hover.activeObjects().length) {
      return this.hover.activeObjects();
    }
    return this.useAllObjects ? this.objectsOnPage : this.objects;
  }

  protected refreshPointerState(target?: StringObject): void {
    if (!target) {
      this.objectDeltaData.clear();
    }

    const mx = this.data.cursor.targetX;
    const my = this.data.cursor.targetY;

    const targets = target ? [target] : this.getCursorTargets();

    const maxDistanceSq =
      !this.nearOnly && this.maxDistanceMultiplier > 0
        ? Math.pow(this.maxDistanceMultiplier * this.data.viewport.windowWidth, 2)
        : null;

    for (const obj of targets) {
      const { cx, cy } = this.centers.getCenter(obj);
      const dx = mx - cx;
      const dy = my - cy;
      const d2 = dx * dx + dy * dy;

      if (
        this.nearOnly &&
        d2 > this.data.viewport.windowWidth * this.data.viewport.windowWidth &&
        !this.hover.isActive(obj)
      )
        continue;

      if (!this.nearOnly && maxDistanceSq !== null && d2 > maxDistanceSq) continue;

      obj.setProperty("dx", dx);
      obj.setProperty("dy", dy);
      obj.setProperty("dist", Math.sqrt(d2));
    }
  }

  private scrollUpdateScheduled = false;

  protected scheduleCursorUpdate(): void {
    if (this.scrollUpdateScheduled) return;
    this.scrollUpdateScheduled = true;
    frameDOM.mutate(() => {
      this.scrollUpdateScheduled = false;
      this.refreshPointerState();
      styleTxn.run(() => {
        this.onCursorScrollUpdate();
      });
    });
  }

  // Modules can override to react after scroll-triggered pointer refresh
  protected onCursorScrollUpdate(): void {}
}

